/**
 * FormateurPhase3Panel — panneau supervision Phase 3.
 *
 * 11/02/2026. Fournit 3 briques :
 *   1. Cohortes nommées : list + create + delete + toggle membres
 *   2. Broadcast messagerie : envoyer un message à une cohorte entière
 *   3. Scénarios pushables : injecter un choc pétrolier / grève / ZFE / crue
 *      dans le jeu d'une cohorte
 *
 * Composant volontairement autonome (pas de deps SupervisionView) pour
 * être testable et remplaçable indépendamment.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Trash2, Megaphone, Zap, ChevronRight, Loader2, Check, X, AlertCircle } from 'lucide-react';
import CohortComparator from './CohortComparator';
import FormateurDebriefingSection from './FormateurDebriefingSection';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TONE_OPTIONS = [
  { id: 'encouragement', label: 'Encouragement' },
  { id: 'advice', label: 'Conseil' },
  { id: 'warning', label: 'Avertissement' },
];

const SCENARIO_OPTIONS = [
  { id: 'fuel_shock', label: 'Choc pétrolier', hint: '+40% carburant pendant 30j', defaultMag: 0.40, defaultDur: 30 },
  { id: 'strike', label: 'Grève sectorielle', hint: 'Circulation bloquée 5j', defaultMag: 1.0, defaultDur: 5 },
  { id: 'zfe_ban', label: 'ZFE — interdiction thermique', hint: 'Pénalité revenus -20% (si aucun EV) pendant 10j', defaultMag: 1.0, defaultDur: 10 },
  { id: 'crue', label: 'Crue majeure', hint: 'Capex forcé (défaut 20 000 €)', defaultMag: 20000, defaultDur: 1 },
];

async function apiFetch(path, options = {}) {
  const resp = await fetch(`${BACKEND}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${resp.status}`);
  }
  return resp.json();
}

export default function FormateurPhase3Panel({ apprentices = [] }) {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCohortId, setExpandedCohortId] = useState(null);
  const [newCohortName, setNewCohortName] = useState('');
  const [newCohortDesc, setNewCohortDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type, text }

  const showFeedback = (type, text) => {
    setFeedback({ type, text });
    setTimeout(() => setFeedback(null), 4000);
  };

  const reloadCohorts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/master/cohorts');
      setCohorts(data.items || []);
    } catch (err) {
      showFeedback('error', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reloadCohorts(); }, [reloadCohorts]);

  const createCohort = async () => {
    const name = newCohortName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await apiFetch('/api/master/cohorts', {
        method: 'POST',
        body: JSON.stringify({ name, description: newCohortDesc.trim() }),
      });
      setNewCohortName('');
      setNewCohortDesc('');
      showFeedback('success', `Cohorte « ${name} » créée.`);
      reloadCohorts();
    } catch (err) {
      showFeedback('error', err.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteCohort = async (id, name) => {
    if (!window.confirm(`Supprimer la cohorte « ${name} » ? Les apprentis ne sont PAS supprimés.`)) return;
    try {
      await apiFetch(`/api/master/cohorts/${id}`, { method: 'DELETE' });
      showFeedback('success', 'Cohorte supprimée.');
      reloadCohorts();
    } catch (err) {
      showFeedback('error', err.message);
    }
  };

  const toggleMember = async (cohort, apprenticeKey) => {
    const isMember = cohort.apprentice_keys.includes(apprenticeKey);
    const next = isMember
      ? cohort.apprentice_keys.filter((k) => k !== apprenticeKey)
      : [...cohort.apprentice_keys, apprenticeKey];
    try {
      await apiFetch(`/api/master/cohorts/${cohort.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ apprentice_keys: next }),
      });
      reloadCohorts();
    } catch (err) {
      showFeedback('error', err.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="formateur-phase3-panel">
      {/* Feedback banner */}
      {feedback && (
        <div
          className={`rounded p-2 text-xs flex items-center gap-2 ${
            feedback.type === 'success'
              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
              : 'bg-red-500/15 text-red-300 border border-red-500/40'
          }`}
          data-testid={`formateur-phase3-feedback-${feedback.type}`}
        >
          {feedback.type === 'success' ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {feedback.text}
        </div>
      )}

      {/* ============ Cohortes ============ */}
      <section className="bg-ink-800/60 border border-ink-600 rounded-lg p-4" data-testid="cohorts-section">
        <header className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-accent" />
          <h3 className="font-display text-sm font-bold tracking-tight uppercase text-foreground">
            Cohortes ({cohorts.length})
          </h3>
        </header>
        <p className="text-xs text-slate-400 mb-3">
          Regroupez vos apprentis en cohortes nommées (classes, groupes TD, promos) pour cibler vos messages et scénarios.
        </p>

        {/* Create form */}
        <div className="bg-ink-900/50 border border-ink-600 rounded p-3 mb-3 space-y-2" data-testid="cohort-create-form">
          <input
            type="text"
            placeholder="Nom de la cohorte (ex : BTS Log 2026-A)"
            value={newCohortName}
            onChange={(e) => setNewCohortName(e.target.value)}
            maxLength={80}
            className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
            data-testid="cohort-create-name"
          />
          <input
            type="text"
            placeholder="Description (optionnel)"
            value={newCohortDesc}
            onChange={(e) => setNewCohortDesc(e.target.value)}
            maxLength={400}
            className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-400 focus:outline-none focus:border-accent"
            data-testid="cohort-create-desc"
          />
          <button
            onClick={createCohort}
            disabled={!newCohortName.trim() || creating}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded bg-accent text-white hover:bg-accent/80 disabled:opacity-40"
            data-testid="cohort-create-submit"
          >
            {creating && <Loader2 className="w-3 h-3 animate-spin" />}
            <Plus className="w-3.5 h-3.5" />
            Créer la cohorte
          </button>
        </div>

        {/* Cohort list */}
        {loading ? (
          <div className="text-xs text-slate-500 italic">Chargement…</div>
        ) : cohorts.length === 0 ? (
          <div className="text-xs text-slate-500 italic" data-testid="cohorts-empty">
            Aucune cohorte pour l&apos;instant.
          </div>
        ) : (
          <ul className="space-y-2">
            {cohorts.map((c) => (
              <li key={c.id} className="bg-ink-900/40 border border-ink-600 rounded" data-testid={`cohort-row-${c.id}`}>
                <div className="flex items-center justify-between px-3 py-2">
                  <button
                    onClick={() => setExpandedCohortId(expandedCohortId === c.id ? null : c.id)}
                    className="flex items-center gap-2 text-left flex-1"
                    data-testid={`cohort-toggle-${c.id}`}
                  >
                    <ChevronRight className={`w-3 h-3 text-slate-500 transition-transform ${expandedCohortId === c.id ? 'rotate-90' : ''}`} />
                    <div>
                      <div className="text-xs font-bold text-foreground">{c.name}</div>
                      <div className="text-[10px] text-slate-500">
                        {c.apprentice_keys.length} apprenti·s · {c.description || 'Sans description'}
                      </div>
                    </div>
                  </button>
                  <button aria-label="Supprimer"
                    onClick={() => deleteCohort(c.id, c.name)}
                    className="text-slate-500 hover:text-red-400 p-1"
                    title="Supprimer"
                    data-testid={`cohort-delete-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {expandedCohortId === c.id && (
                  <div className="px-3 py-2 border-t border-ink-600 space-y-1" data-testid={`cohort-members-${c.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Membres</div>
                    {apprentices.length === 0 ? (
                      <div className="text-xs text-slate-500 italic">Aucun apprenti dans votre organisation.</div>
                    ) : apprentices.map((a) => {
                      const isMember = c.apprentice_keys.includes(a.license_key);
                      return (
                        <label key={a.license_key} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer hover:text-foreground">
                          <input
                            type="checkbox"
                            checked={isMember}
                            onChange={() => toggleMember(c, a.license_key)}
                            className="accent-accent"
                            data-testid={`cohort-member-toggle-${c.id}-${a.license_key}`}
                          />
                          <span>{a.holder_name}</span>
                          <span className="text-[10px] text-slate-500 font-mono">({a.license_key.slice(0, 6)})</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ============ Broadcast ============ */}
      <BroadcastSection cohorts={cohorts} onFeedback={showFeedback} />

      {/* ============ Comparateur pairs (Phase 3 v2) ============ */}
      <CohortComparator cohorts={cohorts} />

      {/* ============ Débriefing collectif (Phase 3 v2) ============ */}
      <FormateurDebriefingSection cohorts={cohorts} onFeedback={showFeedback} />

      {/* ============ Scenarios ============ */}
      <ScenarioPushSection cohorts={cohorts} onFeedback={showFeedback} />
    </div>
  );
}

function BroadcastSection({ cohorts, onFeedback }) {
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [body, setBody] = useState('');
  const [tone, setTone] = useState('encouragement');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!selectedCohortId || body.trim().length < 3) return;
    setSending(true);
    try {
      const res = await apiFetch('/api/master/notes/broadcast', {
        method: 'POST',
        body: JSON.stringify({
          body: body.trim(),
          tone,
          cohort_id: selectedCohortId,
        }),
      });
      onFeedback('success', `Message envoyé à ${res.sent} apprenti·s.`);
      setBody('');
    } catch (err) {
      onFeedback('error', err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-ink-800/60 border border-ink-600 rounded-lg p-4" data-testid="broadcast-section">
      <header className="flex items-center gap-2 mb-3">
        <Megaphone className="w-4 h-4 text-sky-400" />
        <h3 className="font-display text-sm font-bold tracking-tight uppercase text-foreground">
          Message groupé
        </h3>
      </header>
      <p className="text-xs text-slate-400 mb-3">
        Envoyez un même message à tous les apprentis d&apos;une cohorte (fan-out : chacun reçoit une copie individuelle dans sa messagerie).
      </p>
      <div className="space-y-2">
        <select
          value={selectedCohortId}
          onChange={(e) => setSelectedCohortId(e.target.value)}
          className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
          data-testid="broadcast-cohort-select"
        >
          <option value="">— Choisir une cohorte —</option>
          {cohorts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.apprentice_keys.length})
            </option>
          ))}
        </select>
        <select
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
          data-testid="broadcast-tone-select"
        >
          {TONE_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
        </select>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Rappel : soutenance orale le 15 mars. Préparez vos slides."
          className="w-full bg-ink-950 border border-ink-600 rounded p-2 text-xs text-slate-200 h-24 resize-none focus:outline-none focus:border-accent"
          maxLength={600}
          data-testid="broadcast-body"
        />
        <div className="flex justify-between items-center text-[10px] text-slate-500">
          <span>{body.length} / 600 caractères</span>
          <button
            onClick={send}
            disabled={!selectedCohortId || body.trim().length < 3 || sending}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded bg-sky-500 text-white hover:bg-sky-500/80 disabled:opacity-40"
            data-testid="broadcast-send"
          >
            {sending && <Loader2 className="w-3 h-3 animate-spin" />}
            Envoyer à la cohorte
          </button>
        </div>
      </div>
    </section>
  );
}

function ScenarioPushSection({ cohorts, onFeedback }) {
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [kind, setKind] = useState('fuel_shock');
  const [magnitude, setMagnitude] = useState('');
  const [duration, setDuration] = useState('');
  const [note, setNote] = useState('');
  const [pushing, setPushing] = useState(false);

  const currentOpt = SCENARIO_OPTIONS.find((o) => o.id === kind);

  const push = async () => {
    if (!selectedCohortId) return;
    setPushing(true);
    try {
      const body = {
        kind,
        cohort_id: selectedCohortId,
        note: note.trim(),
      };
      if (magnitude !== '') body.magnitude = parseFloat(magnitude);
      if (duration !== '') body.duration_days = parseInt(duration, 10);
      const res = await apiFetch('/api/master/scenarios/push', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onFeedback('success', `Scénario « ${currentOpt.label} » poussé à ${res.target_apprentice_keys.length} apprenti·s.`);
      setNote('');
    } catch (err) {
      onFeedback('error', err.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <section className="bg-ink-800/60 border border-ink-600 rounded-lg p-4" data-testid="scenario-push-section">
      <header className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-amber-400" />
        <h3 className="font-display text-sm font-bold tracking-tight uppercase text-foreground">
          Scénario pédagogique
        </h3>
      </header>
      <p className="text-xs text-slate-400 mb-3">
        Injectez un événement pédagogique dans la partie de vos apprentis. L&apos;effet est temporaire et visible en jeu.
      </p>
      <div className="space-y-2">
        <select
          value={selectedCohortId}
          onChange={(e) => setSelectedCohortId(e.target.value)}
          className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
          data-testid="scenario-cohort-select"
        >
          <option value="">— Choisir une cohorte —</option>
          {cohorts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.apprentice_keys.length})
            </option>
          ))}
        </select>
        <select
          value={kind}
          onChange={(e) => { setKind(e.target.value); setMagnitude(''); setDuration(''); }}
          className="w-full bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
          data-testid="scenario-kind-select"
        >
          {SCENARIO_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
        {currentOpt && (
          <div className="text-[11px] text-slate-500 italic pl-1">
            Défaut : {currentOpt.hint}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            step="0.01"
            placeholder={`Magnitude (défaut ${currentOpt?.defaultMag})`}
            value={magnitude}
            onChange={(e) => setMagnitude(e.target.value)}
            className="bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
            data-testid="scenario-magnitude"
          />
          <input
            type="number"
            step="1"
            placeholder={`Durée jours (défaut ${currentOpt?.defaultDur})`}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="bg-ink-950 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-accent"
            data-testid="scenario-duration"
          />
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note pédagogique optionnelle affichée à l'apprenti (ex : Analysez l'impact sur votre BFR)"
          className="w-full bg-ink-950 border border-ink-600 rounded p-2 text-xs text-slate-200 h-16 resize-none focus:outline-none focus:border-accent"
          maxLength={400}
          data-testid="scenario-note"
        />
        <button
          onClick={push}
          disabled={!selectedCohortId || pushing}
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded bg-amber-500 text-black hover:bg-amber-500/80 disabled:opacity-40"
          data-testid="scenario-push-submit"
        >
          {pushing && <Loader2 className="w-3 h-3 animate-spin" />}
          Pousser le scénario
        </button>
      </div>
    </section>
  );
}
